import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Target, TrendingUp, X } from 'lucide-react';
import { jobsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Tiny banner on /dashboard + /workbench that nudges recruiters toward their
// monthly closure goal. Smart messaging: behind / on-track / achieved.
// Hides for non-recruiter roles by default; managers can opt in by passing `forceShow`.
const GoalBanner = ({ forceShow = false }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    // Per-day dismissal — re-shows next day to keep the nudge alive without being noisy
    const key = `goal-banner-dismissed-${new Date().toDateString()}`;
    return typeof window !== 'undefined' && localStorage.getItem(key) === '1';
  });

  const isRecruiter = user?.role === 'recruiter';
  const visible = forceShow || isRecruiter;

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    jobsApi
      .goalProgress()
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [visible]);

  if (!visible || dismissed) return null;
  if (loading || !data) return null;
  if (!data.goal || data.goal <= 0) return null;

  const { goal, closures_done, behind, achieved, on_track, progress_pct, days_remaining } = data;

  const handleDismiss = () => {
    const key = `goal-banner-dismissed-${new Date().toDateString()}`;
    try { localStorage.setItem(key, '1'); } catch { /* ignore quota errors */ }
    setDismissed(true);
  };

  // ---------- Variant selection ----------
  let variant, icon, title, sub;
  if (achieved) {
    variant = 'achieved';
    icon = Trophy;
    title = `🎯 Goal smashed! ${closures_done}/${goal} closures this month`;
    sub = closures_done > goal
      ? `You're ${closures_done - goal} ahead — the bar just moved.`
      : 'Keep the streak going through end of month.';
  } else if (closures_done === 0) {
    variant = 'cold';
    icon = Target;
    title = `Get the first one home — ${goal} closures to hit your monthly goal`;
    sub = `${days_remaining} day${days_remaining === 1 ? '' : 's'} left in the month.`;
  } else if (on_track) {
    variant = 'ontrack';
    icon = TrendingUp;
    title = `On track — ${closures_done}/${goal} closures this month`;
    sub = `${behind} to go · ${days_remaining} day${days_remaining === 1 ? '' : 's'} left.`;
  } else {
    variant = 'behind';
    icon = Target;
    title = `You are ${behind} closure${behind === 1 ? '' : 's'} behind goal`;
    sub = `${closures_done}/${goal} done · ${days_remaining} day${days_remaining === 1 ? '' : 's'} left in the month.`;
  }

  const Icon = icon;
  const variantClass = {
    achieved: 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-300 text-emerald-900',
    ontrack: 'bg-gradient-to-r from-teal-50 to-sky-50 border-teal-200 text-teal-900',
    cold: 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 text-amber-900',
    behind: 'bg-gradient-to-r from-rose-50 to-amber-50 border-rose-200 text-rose-900',
  }[variant];
  const fillClass = {
    achieved: 'bg-emerald-500',
    ontrack: 'bg-teal-500',
    cold: 'bg-amber-500',
    behind: 'bg-rose-500',
  }[variant];

  return (
    <div
      className={`relative flex items-center gap-3 px-3 py-2 rounded-lg border ${variantClass} shadow-sm`}
      data-testid="goal-banner"
      data-variant={variant}
      role="status"
    >
      <div className="w-8 h-8 rounded-md bg-white/70 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate" data-testid="goal-banner-title">{title}</div>
        <div className="text-[11px] text-slate-600 truncate" data-testid="goal-banner-sub">{sub}</div>
      </div>
      {/* Compact progress bar */}
      <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
        <div className="w-24 h-1.5 bg-white/60 rounded-full overflow-hidden">
          <div
            className={`h-full ${fillClass} transition-all`}
            style={{ width: `${Math.min(100, progress_pct)}%` }}
          />
        </div>
        <span className="text-[11px] font-mono font-semibold w-10 text-right">{Math.round(progress_pct)}%</span>
      </div>
      <Link
        to="/my-performance"
        className="text-[11px] font-semibold text-slate-700 hover:text-slate-900 hover:underline whitespace-nowrap"
        data-testid="goal-banner-cta"
      >
        View details →
      </Link>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss for today"
        className="text-slate-400 hover:text-slate-700 flex-shrink-0"
        data-testid="goal-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default GoalBanner;
